/**
 * InviteModalView
 *
 * The invite dialog as a picture of its props. `InviteModal` owns the session
 * user, the form state and the send; this only draws them, so every state can
 * be shown on its own (and in Storybook) without a store or a network.
 */

import { useId } from 'react';

import { APP_INVITATION_COPY } from '@personal-trainer-app/shared/src/config/app-invitation.config';
import {
  describeInvitationsLeft,
  invitationPips,
} from '@personal-trainer-app/shared/src/utils/invitation/invitation-quota';
import { inviteDialogView } from '@personal-trainer-app/shared/src/utils/invitation/invite-form';

import type { InviteModalViewProps, QuotaPipsProps } from './InviteModal.types';

import { FormInput } from '@/components/forms';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { SectionLabel, Text } from '@/components/ui/primitives';

/** One pip per invitation; the lit ones are what is left. */
function QuotaPips({ left, large = false }: QuotaPipsProps): React.JSX.Element {
  return (
    <div
      className={`flex gap-1 ${large ? 'justify-center' : ''}`}
      aria-hidden="true"
    >
      {invitationPips(left).map((isLeft, index) => (
        <span
          key={index}
          className={`rounded-sm border ${large ? 'h-7 w-4' : 'h-5 w-3'} ${
            isLeft
              ? 'border-primary-300 bg-primary-300'
              : 'border-stroke-strong'
          }`}
        />
      ))}
    </div>
  );
}

export function InviteModalView({
  isOpen,
  onClose,
  username,
  left,
  form,
  onEmailChange,
  onSubmit,
}: InviteModalViewProps): React.JSX.Element {
  const formId = useId();
  const spent = inviteDialogView(left) === 'spent';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={APP_INVITATION_COPY.title}
      size="sm"
      // The actions live in the footer so they stay in reach on a phone,
      // where the body scrolls; Send submits the body's form by its id.
      footer={
        spent ? (
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              form={formId}
              loading={form.isSending}
            >
              {APP_INVITATION_COPY.submit}
            </Button>
          </>
        )
      }
    >
      {spent ? (
        <div className="pt-2 text-center" data-testid="invite-spent">
          <QuotaPips left={0} large />
          <Text as="p" variant="heading-sm" className="mt-4">
            {APP_INVITATION_COPY.spentTitle}
          </Text>
          <Text as="p" variant="body-sm" color="secondary" className="mt-2">
            {APP_INVITATION_COPY.spentBody}
          </Text>
        </div>
      ) : (
        // A form so Enter submits: it is a one-field dialog.
        <form id={formId} onSubmit={onSubmit} noValidate>
          <Text as="p" variant="body-sm" color="secondary">
            {APP_INVITATION_COPY.lede(username)}
          </Text>

          <Card className="mt-4 flex items-center gap-4 px-4 py-3">
            <div className="min-w-0 flex-1">
              <SectionLabel as="p">
                {APP_INVITATION_COPY.quotaLabel}
              </SectionLabel>
              <p
                className="mt-0.5 font-mono text-body-md text-neutral-50"
                data-testid="invite-quota"
              >
                {describeInvitationsLeft(left)}
              </p>
            </div>
            <QuotaPips left={left} />
          </Card>

          <div className="mt-4">
            <FormInput
              name="invite-email"
              type="email"
              label={APP_INVITATION_COPY.fieldLabel}
              placeholder={APP_INVITATION_COPY.placeholder}
              value={form.email}
              onChange={onEmailChange}
              error={form.fieldError ?? undefined}
              helpText={form.fieldError ? undefined : APP_INVITATION_COPY.help}
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          {form.hasFailed && (
            <Alert
              variant="error"
              title={APP_INVITATION_COPY.failedTitle}
              className="mt-4"
            >
              {APP_INVITATION_COPY.failedBody}
            </Alert>
          )}
        </form>
      )}
    </Modal>
  );
}

export default InviteModalView;
